import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Trash2, ArrowRight, ShoppingBag } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { formatPrice, productUrl } from "@/lib/storefront";

export default function MiniCart() {
  const { items, open, setOpen, subtotal, count, updateQty, removeItem } = useCart();
  const nav = useNavigate();
  const currency = items[0]?.currency || "USD";
  const freeShippingThreshold = 75;
  const remaining = Math.max(0, freeShippingThreshold - subtotal);

  if (!open) return null;

  const goCheckout = () => { setOpen(false); nav("/checkout"); };
  const goCart = () => { setOpen(false); nav("/cart"); };

  return (
    <div className="fixed inset-0 z-50" data-testid="mini-cart">
      <div className="absolute inset-0 bg-[#1A1A1A]/30 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <aside className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-[#F2EDDF] shadow-[0_0_60px_rgba(0,0,0,0.12)]">
        <div className="flex items-center justify-between border-b border-[#DDD6C5] px-6 py-5">
          <div className="font-display text-xl">Your bag <span className="ml-1 text-sm text-[#2D2823]">({count})</span></div>
          <button onClick={() => setOpen(false)} aria-label="Close" data-testid="mini-cart-close"><X size={22} /></button>
        </div>

        {remaining > 0 && subtotal > 0 && (
          <div className="bg-[#E8E2D2] px-6 py-3 text-center text-xs text-[#2D2823]">
            Add <span className="font-semibold text-[#1A1A1A]">{formatPrice(remaining, currency)}</span> more for free delivery
            <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-white">
              <div className="h-full bg-[#7D8471]" style={{ width: `${Math.min(100, (subtotal / freeShippingThreshold) * 100)}%` }} />
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <ShoppingBag size={48} className="mb-4 text-[#2D2823]/30" />
              <div className="font-display text-lg">Your bag is empty</div>
              <p className="mt-1 text-sm text-[#2D2823]">Start exploring to find pieces you'll love.</p>
              <button onClick={() => { setOpen(false); nav("/catalog"); }} className="mt-6 rounded-full bg-[#1A1A1A] px-6 py-2.5 text-sm text-white hover:bg-[#7D8471]">
                Shop products
              </button>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((it) => (
                <li key={it.product_id} className="flex gap-3" data-testid={`cart-item-${it.product_id}`}>
                  <Link to={productUrl(it)} onClick={() => setOpen(false)} className="block h-24 w-20 flex-shrink-0 overflow-hidden rounded bg-[#E8E2D2]">
                    {it.image ? <img src={it.image} alt="" className="h-full w-full object-cover" /> : null}
                  </Link>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <Link to={productUrl(it)} onClick={() => setOpen(false)} className="line-clamp-2 text-sm text-[#1A1A1A] hover:text-[#7D8471]">{it.title}</Link>
                    <div className="mt-auto flex items-end justify-between">
                      <div className="flex items-center gap-1 rounded-full border border-[#DDD6C5]">
                        <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="px-2 py-1 text-sm">−</button>
                        <span className="px-1 text-sm store-nums" data-testid={`qty-${it.product_id}`}>{it.quantity}</span>
                        <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="px-2 py-1 text-sm">+</button>
                      </div>
                      <div className="text-sm store-nums">{formatPrice(it.price * it.quantity, it.currency)}</div>
                    </div>
                  </div>
                  <button onClick={() => removeItem(it.product_id)} aria-label="Remove" className="self-start p-1 text-[#2D2823] hover:text-[#1A1A1A]"><Trash2 size={14} /></button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[#DDD6C5] p-6">
            <div className="flex items-center justify-between text-sm text-[#2D2823]">
              <span>Subtotal</span>
              <span className="store-nums font-medium text-[#1A1A1A]">{formatPrice(subtotal, currency)}</span>
            </div>
            <p className="mt-1 text-xs text-[#2D2823]">Shipping and taxes calculated at checkout</p>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button onClick={goCart} className="rounded-full border border-[#1A1A1A] px-4 py-3 text-sm text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white">View bag</button>
              <button onClick={goCheckout} className="rounded-full bg-[#1A1A1A] px-4 py-3 text-sm text-white hover:bg-[#7D8471]" data-testid="mini-cart-checkout">
                Checkout <ArrowRight size={14} className="ml-1 inline-block" />
              </button>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
